/**
 * Innings state machine.
 *
 * Written as a generator that runs until it needs a decision from the captain,
 * yields, and resumes with the answer. That shape gives us two things:
 *
 *  - The UI drives it directly: reveal the yielded balls, prompt for the next
 *    bowler, resume.
 *  - Replay is the same code path. Feed the recorded decisions back in and the
 *    innings reconstructs exactly, which is how the Worker verifies a submitted
 *    result and how a match resumes after the app is closed. No engine state is
 *    ever serialised -- re-running from `(seed, decisionLog)` costs well under a
 *    millisecond.
 */

import { phaseOf } from './baselines.generated.ts';
import { resolveBall, assignCatcher } from './ball.ts';
import { rollInningsForm } from './form.ts';
import { createRng, deriveSeed, type Rng } from './rng.ts';
import type { Coefficients } from './coefficients.ts';
import type { Aggression, BallOutcome, DismissalKind, Phase, Player } from './types.ts';

/** Overs in a T20 innings, and the per-bowler cap. */
export const OVERS_PER_INNINGS = 20;
export const MAX_OVERS_PER_BOWLER = 4;
export const BALLS_PER_OVER = 6;

/**
 * The shape of an innings.
 *
 * Exists because a super over is the same cricket in a different container: one
 * over, two wickets, and it ends. Everything else -- the ball model, the
 * dismissal mix, the chase logic -- is identical, so parameterising the
 * container is far safer than a second code path that would drift.
 */
export interface InningsFormat {
  readonly overs: number;
  /** The innings ends on this many wickets. */
  readonly maxWickets: number;
  /**
   * Forces the phase instead of deriving it from the over index.
   *
   * A super over is death cricket from the first ball -- best bowler, maximum
   * intent, fielders back. Deriving the phase would call it a powerplay and
   * price it like the first over of an innings, which is the opposite of how it
   * is actually played.
   */
  readonly forcePhase?: Phase;
}

export const T20_FORMAT: InningsFormat = { overs: OVERS_PER_INNINGS, maxWickets: 10 };

/**
 * One over, two wickets.
 *
 * Two rather than three because a side is all out when it loses its second
 * wicket -- there is no third batter to partner the survivor.
 */
export const SUPER_OVER_FORMAT: InningsFormat = { overs: 1, maxWickets: 2, forcePhase: 'DEATH' };

export interface BatterCard {
  readonly player: Player;
  runs: number;
  balls: number;
  fours: number;
  sixes: number;
  out: boolean;
  dismissal?: { kind: DismissalKind; bowler?: string; fielder?: string };
}

export interface BowlerCard {
  readonly player: Player;
  balls: number;
  runs: number;
  wickets: number;
  maidens: number;
}

/** One delivery, carrying everything commentary needs. */
export interface BallEvent {
  readonly overIndex: number;
  /** 1-based legal ball within the over. Repeats on an extra, as scorecards do. */
  readonly ballInOver: number;
  readonly isLegal: boolean;
  readonly bowler: Player;
  readonly striker: Player;
  readonly nonStriker: Player;
  readonly runsOffBat: number;
  readonly extra?: { kind: string; runs: number };
  readonly wicket?: { kind: DismissalKind; batter: Player; bowler?: Player; fielder?: Player };
  readonly freeHit: boolean;
  readonly score: { runs: number; wickets: number };
  readonly strikerCard: { runs: number; balls: number };
  readonly bowlerFigures: { balls: number; runs: number; wickets: number };
  readonly partnership: { runs: number; balls: number };
  readonly overRuns: number;
  readonly dotsInARow: number;
  readonly chase?: { runsRequired: number; ballsRemaining: number };
  readonly milestone?: Milestone;
}

export type Milestone =
  | 'FIFTY'
  | 'HUNDRED'
  | 'PARTNERSHIP_FIFTY'
  | 'MAIDEN_OVER'
  | 'BOWLER_THREE_FOR'
  | 'HAT_TRICK';

/** What the engine needs answered before it can continue. */
export type Decision =
  | { readonly type: 'SELECT_BOWLER'; readonly overIndex: number; readonly eligible: readonly Player[] }
  | {
      readonly type: 'STRATEGIC_TIMEOUT';
      readonly overIndex: number;
      /** True on the last over of the window, when the break is enforced. */
      readonly forced: boolean;
      readonly side: TimeoutSide;
    };

export type TimeoutSide = 'bowling' | 'batting';

/**
 * The two strategic-break windows, 0-based over indices.
 *
 * Bowling side takes theirs between overs 7 and 10 to reset plans or break a
 * partnership; batting side between 13 and 16 to map out the death overs.
 */
export const TIMEOUT_WINDOWS: ReadonlyArray<{ side: TimeoutSide; from: number; to: number }> = [
  { side: 'bowling', from: 6, to: 9 },
  { side: 'batting', from: 12, to: 15 },
];

export type Answer =
  | { readonly type: 'BOWLER'; readonly playerId: string }
  | { readonly type: 'AGGRESSION'; readonly value: Aggression }
  /** Declines an optional strategic break; ignored when the break is forced. */
  | { readonly type: 'DECLINE' };

export interface Yielded {
  /**
   * Deliveries bowled since the previous yield -- in practice, the over that
   * just finished. This is how a UI streams a match: the generator computes a
   * whole over between decisions, so without these the caller sees nothing
   * until the innings ends.
   *
   * Empty on the first yield of an innings, and on the second of two yields at
   * the same over boundary (a strategic break followed by a bowler choice).
   * The final over's deliveries never appear here, because the innings ends
   * without asking anything further -- read them from `InningsResult.events`.
   */
  readonly events: readonly BallEvent[];
  readonly decision: Decision;
}

export interface InningsResult {
  readonly runs: number;
  readonly wickets: number;
  readonly legalBalls: number;
  readonly batting: readonly BatterCard[];
  readonly bowling: readonly BowlerCard[];
  readonly events: readonly BallEvent[];
  /** Set when the innings ended because the target was passed. */
  readonly chaseCompleted: boolean;
}

export interface InningsConfig {
  readonly seed: string;
  readonly inningsIndex: 0 | 1;
  readonly battingXI: readonly Player[];
  readonly bowlingXI: readonly Player[];
  readonly coefficients: Coefficients;
  /** Runs needed to win. Omitted in the first innings. */
  readonly target?: number;
  /** Batting side's slider. Bowling aggression is not modelled separately in v1. */
  readonly aggression?: Aggression;
  /** Defaults to a full T20 innings. */
  readonly format?: InningsFormat;
}

/**
 * How many overs each bowler may realistically bowl.
 *
 * Capped by what they actually bowled in real matches, not a flat four. A
 * part-timer who never bowled more than two overs in a season should not be
 * handed a full quota here -- allowing it let the CPU give nine middle overs to
 * medium pace and pushed middle-overs scoring 6% above real.
 *
 * Falls back to a flat quota if the observed caps cannot cover twenty overs,
 * so a thin attack is stretched rather than stranded.
 */
export function bowlerCapacities(
  bowlingXI: readonly Player[],
  format: InningsFormat = T20_FORMAT,
): ReadonlyMap<string, number> {
  const caps = new Map<string, number>();
  let total = 0;
  for (const p of bowlingXI) {
    if (p.bowlOvers < 1) continue;
    const cap = Math.min(MAX_OVERS_PER_BOWLER, p.bowlOvers);
    caps.set(p.id, cap);
    total += cap;
  }
  // Only stretch the attack when the observed caps genuinely cannot cover the
  // innings. A super over needs one over, so this never fires for it.
  if (total < format.overs) {
    for (const p of bowlingXI) {
      if (p.bowlOvers >= 1) caps.set(p.id, MAX_OVERS_PER_BOWLER);
    }
  }
  return caps;
}

/**
 * Bowlers who may legally bowl the next over.
 *
 * Eligibility comes from `bowlOvers` -- observed from real ball-by-ball data --
 * never from the role label, which types R Ashwin and Sam Curran as "BAT".
 */
export function eligibleBowlers(
  bowlingXI: readonly Player[],
  oversBowled: ReadonlyMap<string, number>,
  lastBowlerId: string | undefined,
  capacities: ReadonlyMap<string, number> = bowlerCapacities(bowlingXI),
): readonly Player[] {
  return bowlingXI.filter(
    (p) =>
      (capacities.get(p.id) ?? 0) > 0 &&
      (oversBowled.get(p.id) ?? 0) < (capacities.get(p.id) ?? 0) &&
      p.id !== lastBowlerId,
  );
}

/**
 * Were this bowler's two previous legal deliveries also wickets credited to
 * them? Called as the third wicket falls, before its event is recorded.
 *
 * Only legal deliveries count -- a wide between two wickets does not break a
 * hat-trick.
 */
function isHatTrick(events: readonly BallEvent[], bowlerId: string): boolean {
  const theirs: BallEvent[] = [];
  for (let i = events.length - 1; i >= 0 && theirs.length < 2; i--) {
    const e = events[i];
    if (e.bowler.id !== bowlerId || !e.isLegal) continue;
    theirs.push(e);
  }
  return theirs.length === 2 && theirs.every((e) => e.wicket?.bowler?.id === bowlerId);
}

export function* simulateInnings(
  config: InningsConfig,
): Generator<Yielded, InningsResult, Answer> {
  const { battingXI, bowlingXI, coefficients: co, target, inningsIndex } = config;
  const format = config.format ?? T20_FORMAT;

  if (battingXI.length !== 11) throw new Error(`batting XI must have 11, got ${battingXI.length}`);
  if (bowlingXI.length !== 11) throw new Error(`bowling XI must have 11, got ${bowlingXI.length}`);

  const ballRng = createRng(deriveSeed(config.seed, `innings${inningsIndex}:balls`));
  const formRng = createRng(deriveSeed(config.seed, `innings${inningsIndex}:form`));
  const fieldRng = createRng(deriveSeed(config.seed, `innings${inningsIndex}:field`));

  const batForm = rollInningsForm(battingXI, co, formRng);
  const bowlForm = rollInningsForm(bowlingXI, co, formRng);

  const batting: BatterCard[] = battingXI.map((player) => ({
    player, runs: 0, balls: 0, fours: 0, sixes: 0, out: false,
  }));
  const bowling = new Map<string, BowlerCard>(
    bowlingXI.map((player) => [player.id, { player, balls: 0, runs: 0, wickets: 0, maidens: 0 }]),
  );

  let runs = 0;
  let wickets = 0;
  let legalBalls = 0;
  let strikerIdx = 0;
  let nonStrikerIdx = 1;
  let nextBatterIdx = 2;
  let freeHit = false;
  let dotsInARow = 0;
  let partnershipRuns = 0;
  let partnershipBalls = 0;
  let aggression = config.aggression ?? 0;
  const timeoutsTaken = new Set<TimeoutSide>();

  const oversBowled = new Map<string, number>();
  const capacities = bowlerCapacities(bowlingXI, format);
  let lastBowlerId: string | undefined;
  const allEvents: BallEvent[] = [];

  /**
   * Deliveries bowled since the last yield, handed to the caller with the next
   * decision and then cleared. Filled at the end of each over, once the maiden
   * rewrite below has had its say -- appending per ball would hand out an event
   * object that is about to be replaced.
   */
  let pending: BallEvent[] = [];
  const flushEvents = (): readonly BallEvent[] => {
    const out = pending;
    pending = [];
    return out;
  };

  const chaseState = () =>
    target === undefined
      ? undefined
      : {
          runsRequired: Math.max(0, target - runs),
          ballsRemaining: format.overs * BALLS_PER_OVER - legalBalls,
        };

  const chaseDone = () => target !== undefined && runs >= target;

  for (let overIndex = 0; overIndex < format.overs; overIndex++) {
    if (wickets >= format.maxWickets || chaseDone()) break;

    // --- strategic timeouts --------------------------------------------------
    // Two windows, per the design and IPL practice:
    //   bowling side  overs 7-10  (0-based 6-9)
    //   batting side  overs 13-16 (0-based 12-15)
    // Each is offered on every over of its window so a human captain can call
    // it when they want, and forced at the end of the window if they have not.
    // An earlier version only ever fired the forced break, so the "Call for
    // Strategic Break" button in the design had nothing to hook into.
    for (const window of TIMEOUT_WINDOWS) {
      if (timeoutsTaken.has(window.side)) continue;
      if (overIndex < window.from || overIndex > window.to) continue;

      const forced = overIndex === window.to;
      const answer = yield {
        events: flushEvents(),
        decision: { type: 'STRATEGIC_TIMEOUT', overIndex, forced, side: window.side },
      };

      // Declining is allowed until the window closes; the caller signals that
      // by answering anything other than an aggression setting.
      if (answer?.type === 'AGGRESSION') {
        if (window.side === 'batting') aggression = answer.value;
        timeoutsTaken.add(window.side);
      } else if (forced) {
        timeoutsTaken.add(window.side);
      }
    }

    // --- pick the bowler -----------------------------------------------------
    const eligible = eligibleBowlers(bowlingXI, oversBowled, lastBowlerId, capacities);
    if (eligible.length === 0) {
      throw new Error(
        `no eligible bowler for over ${overIndex + 1}: the XI cannot cover 20 overs. ` +
          `This must be prevented by the draft feasibility guard, not discovered here.`,
      );
    }

    const answer = yield {
      events: flushEvents(),
      decision: { type: 'SELECT_BOWLER', overIndex, eligible },
    };
    const chosen =
      answer?.type === 'BOWLER' ? eligible.find((p) => p.id === answer.playerId) : undefined;
    if (!chosen) {
      throw new Error(
        `invalid bowler selection for over ${overIndex + 1}: ` +
          `${answer?.type === 'BOWLER' ? answer.playerId : 'no answer'} is not eligible`,
      );
    }

    const bowler = chosen;
    const card = bowling.get(bowler.id)!;
    oversBowled.set(bowler.id, (oversBowled.get(bowler.id) ?? 0) + 1);
    lastBowlerId = bowler.id;

    const overEvents: BallEvent[] = [];
    let ballsThisOver = 0;
    let overRuns = 0;

    while (ballsThisOver < BALLS_PER_OVER) {
      if (wickets >= format.maxWickets || chaseDone()) break;

      const striker = battingXI[strikerIdx];
      const nonStriker = battingXI[nonStrikerIdx];
      const phase = format.forcePhase ?? phaseOf(overIndex);

      const outcome: BallOutcome = resolveBall(
        {
          phase,
          overIndex,
          striker,
          bowler,
          aggression,
          strikerForm: batForm.get(striker.id) ?? 1,
          bowlerForm: bowlForm.get(bowler.id) ?? 1,
          wicketsLost: wickets,
          chase: chaseState(),
          isFreeHit: freeHit,
        },
        co,
        ballRng,
      );

      let milestone: Milestone | undefined;
      let wicketInfo: BallEvent['wicket'];
      // The free-hit state that applied to THIS delivery. `freeHit` itself is
      // mutated below for the next ball, so reporting it on the event would
      // describe the wrong delivery.
      const wasFreeHit = freeHit;
      const strikerCard = batting[strikerIdx];

      // Any extra runs (wide, no-ball penalty, bye, legbye) go to the total and
      // are charged to the bowler, whether or not the delivery was legal.
      if (outcome.extra) {
        runs += outcome.extra.runs;
        overRuns += outcome.extra.runs;
        card.runs += outcome.extra.runs;
      }

      // Byes and legbyes are legal deliveries; wides and no-balls are not.
      // Branching on `outcome.extra` rather than `outcome.isLegal` treated
      // byes and legbyes as re-bowled, so overs ran long, the batter was never
      // credited with the ball, and innings delivered more than 120 legal balls.
      if (outcome.isLegal) {
        legalBalls++;
        ballsThisOver++;
        card.balls++;
      }

      if (!outcome.isLegal && !outcome.wicket) {
        // A no-ball is still hittable, so runs off the bat must be credited
        // even though the delivery is re-bowled and no ball faced is recorded.
        const r = outcome.runsOffBat;
        if (r > 0) {
          runs += r;
          overRuns += r;
          card.runs += r;
          strikerCard.runs += r;
          partnershipRuns += r;
          if (r === 4) strikerCard.fours++;
          if (r === 6) strikerCard.sixes++;
          if (r % 2 === 1) [strikerIdx, nonStrikerIdx] = [nonStrikerIdx, strikerIdx];
        }

        // A free hit persists until the next LEGAL delivery -- a wide bowled
        // during a free hit does not consume it. A first pass cleared the flag
        // on any extra, which quietly discarded free hits.
        if (outcome.extra?.kind === 'noball') freeHit = true;
        dotsInARow = 0;
      } else {
        if (outcome.wicket) {
          wickets++;
          strikerCard.out = true;

          // If nobody but the bowler is available to take the catch, it is a
          // caught and bowled, not a catch by the bowler.
          let kind = outcome.wicket.kind;
          let fielder: Player | undefined;
          if (kind === 'caught') {
            const catcher = assignCatcher(bowlingXI, bowler, fieldRng);
            if (catcher) fielder = catcher;
            else kind = 'caught and bowled';
          }

          strikerCard.dismissal = {
            kind,
            bowler: outcome.wicket.creditBowler ? bowler.id : undefined,
            fielder: fielder?.id,
          };
          if (outcome.wicket.creditBowler) card.wickets++;
          wicketInfo = {
            kind,
            batter: striker,
            bowler: outcome.wicket.creditBowler ? bowler : undefined,
            fielder,
          };
          // A run out can fall on a no-ball, which is not a ball faced.
          if (outcome.isLegal) {
            strikerCard.balls++;
            partnershipBalls++;
          }
          // A wicket is not a dot -- runs can be taken on a run out, and the
          // "three dots in a row" commentary line should not count dismissals.
          dotsInARow = 0;

          // Hat-trick outranks the three-for: it is the rarer thing, and both
          // can land on the same delivery.
          if (isHatTrick(allEvents, bowler.id)) milestone = 'HAT_TRICK';
          else if (card.wickets === 3) milestone = 'BOWLER_THREE_FOR';

          // Next batter takes the dismissed player's end.
          if (nextBatterIdx < battingXI.length) {
            strikerIdx = nextBatterIdx++;
          }
          partnershipRuns = 0;
          partnershipBalls = 0;
        } else {
          const r = outcome.runsOffBat;
          runs += r;
          overRuns += r;
          card.runs += r;
          strikerCard.runs += r;
          strikerCard.balls++;
          partnershipRuns += r;
          partnershipBalls++;
          if (r === 4) strikerCard.fours++;
          if (r === 6) strikerCard.sixes++;
          dotsInARow = r === 0 ? dotsInARow + 1 : 0;

          // Milestones fire once, on the ball that crosses the threshold.
          // Recomputing "runs >= 50" every ball would re-fire forever.
          if (strikerCard.runs >= 100 && strikerCard.runs - r < 100) milestone = 'HUNDRED';
          else if (strikerCard.runs >= 50 && strikerCard.runs - r < 50) milestone = 'FIFTY';
          else if (partnershipRuns >= 50 && partnershipRuns - r < 50) {
            milestone = 'PARTNERSHIP_FIFTY';
          }

          if (r % 2 === 1) [strikerIdx, nonStrikerIdx] = [nonStrikerIdx, strikerIdx];
        }
        // A no-ball grants a free hit even when a run out falls on it.
        freeHit = outcome.extra?.kind === 'noball';
      }

      const event: BallEvent = {
        overIndex,
        ballInOver: Math.max(1, ballsThisOver),
        isLegal: outcome.isLegal,
        bowler,
        striker,
        nonStriker,
        runsOffBat: outcome.runsOffBat,
        extra: outcome.extra ? { kind: outcome.extra.kind, runs: outcome.extra.runs } : undefined,
        wicket: wicketInfo,
        freeHit: wasFreeHit,
        score: { runs, wickets },
        // The batter who actually faced this ball. Reading batting[strikerIdx]
        // here would report whoever is on strike *after* rotation or a
        // dismissal, so commentary would print the wrong player's figures.
        strikerCard: { runs: strikerCard.runs, balls: strikerCard.balls },
        bowlerFigures: { balls: card.balls, runs: card.runs, wickets: card.wickets },
        partnership: { runs: partnershipRuns, balls: partnershipBalls },
        overRuns,
        dotsInARow,
        chase: chaseState(),
        milestone,
      };
      overEvents.push(event);
      allEvents.push(event);
    }

    if (overRuns === 0 && ballsThisOver === BALLS_PER_OVER) {
      card.maidens++;
      // Emit the milestone on the over's last ball. A maiden in T20 is rare
      // enough to be worth calling out.
      const last = overEvents.at(-1);
      if (last) {
        const withMilestone: BallEvent = { ...last, milestone: last.milestone ?? 'MAIDEN_OVER' };
        overEvents[overEvents.length - 1] = withMilestone;
        allEvents[allEvents.length - 1] = withMilestone;
      }
    }

    // Hand this over's deliveries to the caller with the next decision. Done
    // here, after the maiden rewrite, so the events queued are final.
    pending.push(...overEvents);

    // Ends swap at the end of an over.
    [strikerIdx, nonStrikerIdx] = [nonStrikerIdx, strikerIdx];
  }

  return {
    runs,
    wickets,
    legalBalls,
    batting,
    bowling: [...bowling.values()],
    events: allEvents,
    chaseCompleted: chaseDone(),
  };
}
